import { z } from 'zod'

const assetPath = z
  .string()
  .regex(/^(?!.*\.\.)(?:[a-zA-Z0-9_-]+\/)*[a-zA-Z0-9_.-]+$/)
const size = z
  .object({
    value: z.number().positive().max(2000),
    unit: z.enum(['%', 'vw', 'vh', 'px']),
    maxPixels: z.number().positive().max(8192).optional()
  })
  .strict()

const keyframe = z
  .object({
    offset: z.number().min(0).max(1),
    translateX: z.number().min(-100).max(100).default(0),
    translateY: z.number().min(-100).max(100).default(0),
    rotate: z.number().min(-360).max(360).default(0),
    scale: z.number().min(0.25).max(2).default(1),
    opacity: z.number().min(0).max(1).optional()
  })
  .strict()

const motion = z
  .object({
    durationMs: z.number().min(2000).max(300000),
    phaseMs: z.number().min(0).max(600000).default(0),
    easing: z
      .enum(['linear', 'ease', 'ease-in', 'ease-out', 'ease-in-out'])
      .default('ease-in-out'),
    direction: z
      .enum(['normal', 'reverse', 'alternate', 'alternate-reverse'])
      .default('alternate'),
    keyframes: z
      .array(keyframe)
      .min(2)
      .max(12)
      .refine(
        frames =>
          frames[0].offset === 0 &&
          frames[frames.length - 1].offset === 1 &&
          frames.every(
            (frame, index) =>
              index === 0 || frame.offset > frames[index - 1].offset
          ),
        'Keyframe offsets must increase from 0 to 1'
      )
  })
  .strict()

const layout = z
  .object({
    space: z.enum(['viewport', 'artwork']),
    left: z.number().min(-100).max(200),
    top: z.number().min(-100).max(200),
    width: size,
    height: size.optional(),
    aspectRatio: z.number().positive().max(10).optional(),
    originX: z.number().min(0).max(100).default(50),
    originY: z.number().min(0).max(100).default(50)
  })
  .strict()

const commonLayer = {
  id: z.string().regex(/^[a-z0-9-]+$/),
  layout,
  opacity: z.number().min(0).max(1).default(1),
  motion: motion.optional()
}

const imageLayer = z
  .object({
    ...commonLayer,
    type: z.literal('image'),
    src: assetPath,
    fit: z.enum(['cover', 'contain', 'fill']).default('contain')
  })
  .strict()

const waterLayer = z
  .object({
    id: commonLayer.id,
    layout,
    opacity: commonLayer.opacity,
    type: z.literal('water'),
    src: assetPath,
    mask: assetPath,
    strength: z.number().min(0).max(6).default(2.5),
    speed: z.number().min(0.1).max(3).default(1),
    shimmer: z.number().min(0).max(0.05).default(0.012)
  })
  .strict()
  .refine(
    layer =>
      layer.layout.height !== undefined ||
      layer.layout.aspectRatio !== undefined,
    'Water layers require height or aspectRatio'
  )

const gradientLayer = z
  .object({
    ...commonLayer,
    type: z.literal('gradient'),
    background: z
      .string()
      .max(2000)
      .regex(/^(?:radial|linear)-gradient\(/)
      .refine(
        value => !/url\s*\(/i.test(value),
        'Use an image layer for assets'
      )
  })
  .strict()
  .refine(
    layer =>
      layer.layout.height !== undefined ||
      layer.layout.aspectRatio !== undefined,
    'Gradient layers require height or aspectRatio'
  )

const range = (min: number, max: number) =>
  z
    .tuple([z.number().min(min).max(max), z.number().min(min).max(max)])
    .refine(([start, end]) => start <= end, 'Range must increase')

const particleLayer = z
  .object({
    id: commonLayer.id,
    layout,
    opacity: commonLayer.opacity,
    type: z.literal('particles'),
    count: z.number().int().min(1).max(64),
    seed: z.number().int().min(1).max(2147483646).default(1),
    appearance: z.discriminatedUnion('kind', [
      z
        .object({
          kind: z.literal('image'),
          sources: z.array(assetPath).min(1).max(4)
        })
        .strict(),
      z
        .object({
          kind: z.literal('dot'),
          color: z.string().regex(/^#[a-fA-F0-9]{6}$/),
          soft: z.boolean().default(false)
        })
        .strict()
    ]),
    emitter: z.object({ x: range(-25, 125), y: range(-25, 125) }).strict(),
    travel: z.object({ x: range(-125, 125), y: range(-125, 125) }).strict(),
    durationMs: range(2000, 120000),
    sizePx: range(1, 96),
    opacityRange: range(0, 1),
    sway: range(0, 40),
    spin: range(-720, 720),
    flutterDeg: z.number().min(0).max(75).default(0)
  })
  .strict()
  .refine(
    layer =>
      layer.layout.height !== undefined ||
      layer.layout.aspectRatio !== undefined,
    'Particle layers require height or aspectRatio'
  )

// Shared Zod field definitions, spread into the full and preview object schemas.
const sceneMetadata = {
  poster: assetPath,
  thumbnail: assetPath,
  backgroundColor: z.string().regex(/^#[a-fA-F0-9]{6}$/),
  canvas: z
    .object({
      width: z.number().positive().max(8192),
      height: z.number().positive().max(8192),
      fit: z.enum(['contain', 'cover']).default('contain')
    })
    .strict()
}

const animatedWallpaperScenePreviewSchema = z.object(sceneMetadata).strict()

export const animatedWallpaperSceneSchema = z
  .object({
    ...sceneMetadata,
    layers: z
      .array(
        z.discriminatedUnion('type', [
          imageLayer,
          gradientLayer,
          particleLayer,
          waterLayer
        ])
      )
      .min(1)
      .max(8)
  })
  .strict()
  .refine(
    scene =>
      new Set(scene.layers.map(layer => layer.id)).size === scene.layers.length,
    'Layer IDs must be unique'
  )
  .refine(
    scene =>
      scene.layers.reduce(
        (total, layer) =>
          total + (layer.type === 'particles' ? layer.count : 0),
        0
      ) <= 96,
    'A scene supports at most 96 particles'
  )
  .refine(
    scene => scene.layers.filter(layer => layer.type === 'water').length <= 1,
    'A scene supports at most one water layer'
  )

const metadata = {
  $schema: z.string().optional(),
  version: z.literal(1),
  id: z.string().regex(/^animated-[a-z0-9-]+$/),
  name: z.string().min(1).max(100),
  localizedNames: z.record(z.string(), z.string()).optional(),
  order: z.number().default(0)
}

export const animatedWallpaperDefinitionSchema = z.discriminatedUnion(
  'renderer',
  [
    animatedWallpaperSceneSchema.safeExtend({
      ...metadata,
      renderer: z.literal('layers')
    }),
    z
      .object({
        ...metadata,
        renderer: z.literal('seasonal'),
        hemisphere: z.enum(['north', 'south']).default('north'),
        seasons: z
          .object({
            spring: animatedWallpaperSceneSchema,
            summer: animatedWallpaperSceneSchema,
            autumn: animatedWallpaperSceneSchema,
            winter: animatedWallpaperSceneSchema
          })
          .strict()
      })
      .strict()
  ]
)

// The desktop catalog only carries scene metadata; layers load on activation.
export const animatedWallpaperPreviewSchema = z.discriminatedUnion('renderer', [
  animatedWallpaperScenePreviewSchema.safeExtend({
    ...metadata,
    renderer: z.literal('layers')
  }),
  z
    .object({
      ...metadata,
      renderer: z.literal('seasonal'),
      hemisphere: z.enum(['north', 'south']).default('north'),
      seasons: z
        .object({
          spring: animatedWallpaperScenePreviewSchema,
          summer: animatedWallpaperScenePreviewSchema,
          autumn: animatedWallpaperScenePreviewSchema,
          winter: animatedWallpaperScenePreviewSchema
        })
        .strict()
    })
    .strict()
])
